import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, MapPin } from "lucide-react";

const REGIONS = [
    {
        code: "GB",
        name: "United Kingdom",
        currency: "GBP",
        symbol: "£",
    },
    {
        code: "CA",
        name: "Canada",
        currency: "CAD",
        symbol: "$",
    },
];

const RegionSelector = ({ className = "" }) => {
    const [region, setRegion] = useState("GB");
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const savedRegion = localStorage.getItem("solksites-region");

        if (savedRegion && REGIONS.some((item) => item.code === savedRegion)) {
            setRegion(savedRegion);
        }
    }, []);

    // Close on outside click and on Escape, so the menu behaves like a native select.
    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    const currentRegion =
        REGIONS.find((item) => item.code === region) || REGIONS[0];

    const handleRegionChange = (regionCode) => {
        setRegion(regionCode);
        localStorage.setItem("solksites-region", regionCode);
        setOpen(false);

        // This will allow the rest of the website
        // to react to the currency change later.
        window.dispatchEvent(
            new CustomEvent("solksites-region-change", {
                detail: { region: regionCode },
            })
        );
    };

    return (
        <div ref={containerRef} className={`relative inline-block ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all"
                aria-expanded={open}
                aria-haspopup="menu"
            >
                <MapPin className="h-4 w-4 text-blue-600 dark:text-blue-400" />

                <span>
                    {currentRegion.code} ({currentRegion.symbol})
                </span>

                <ChevronDown
                    className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""
                        }`}
                />
            </button>

            {open && (
                <div
                    className="absolute right-0 top-full mt-2 z-50 w-60 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl overflow-hidden"
                    role="menu"
                >
                    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                            Select your region
                        </p>
                    </div>

                    <div className="py-1">
                        {REGIONS.map((item) => {
                            const isSelected = region === item.code;

                            return (
                                <button
                                    key={item.code}
                                    type="button"
                                    onClick={() => handleRegionChange(item.code)}
                                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${isSelected
                                            ? "bg-blue-50 dark:bg-blue-950/40"
                                            : "hover:bg-slate-50 dark:hover:bg-slate-800"
                                        }`}
                                    role="menuitem"
                                    aria-current={isSelected}
                                >
                                    <span>
                                        <span
                                            className={`block text-sm font-medium ${isSelected
                                                    ? "text-blue-600 dark:text-blue-400"
                                                    : "text-slate-700 dark:text-slate-200"
                                                }`}
                                        >
                                            {item.name}
                                        </span>
                                        <span className="block text-xs text-slate-400">
                                            {item.currency} ({item.symbol})
                                        </span>
                                    </span>

                                    {isSelected && (
                                        <Check className="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegionSelector;